import axios from "../utils/axios";
import { store } from "../store/store";
import
  {
    updateProgress,
    removeDownload,
    setStatus,
  } from "../store/reducers/downloadSlice";
import mime from "mime-types";

// => 'zip'
interface DownloadTask
{
  id: string;
  name: string;
  url: string;
  abortController: AbortController | null;
  downloadedBytes: number;
  totalBytes: number;
}

class DownloadManager
{
  private tasks: Map<string, DownloadTask> = new Map();

  async downloadFile(
    file: { id: string; name: string; url: string },
    startBytes: number = 0,
  ): Promise<void>
  {
    if (this.tasks.has(file.id)) return;

    const abortController = new AbortController();

    const task: DownloadTask = {
      id: file.id,
      name: file.name,
      url: file.url,
      abortController,
      downloadedBytes: startBytes,
      totalBytes: 0,
    };

    this.tasks.set(file.id, task);
    store.dispatch(setStatus({ id: file.id, status: "downloading" }));

    try
    {
      const headers: Record<string, string> = {};

      if (startBytes > 0)
      {
        headers["Range"] = `bytes=${startBytes}-`;
      }

      const response = await axios.get(file.url, {
        responseType: "blob",
        signal: abortController.signal,
        headers,
        transformResponse: [(data) => data],

        onDownloadProgress: (progressEvent) =>
        {
          if (progressEvent.total)
          {
            const total = progressEvent.total + startBytes;
            const loaded = progressEvent.loaded + startBytes;

            const percent = Math.round((loaded * 100) / total);

            task.downloadedBytes = loaded;
            task.totalBytes = total;

            store.dispatch(updateProgress({ id: file.id, progress: percent }));
          }
        },
      });

      if (response.status !== 200 && response.status !== 206)
      {
        throw new Error("Download failed");
      }

      //  FIX: dùng trực tiếp blob
      const blob = response.data;

      // Resolve filename chuẩn
      const filename = this.resolveFilename(file, response);

      this.triggerDownload(blob, filename);

      store.dispatch(updateProgress({ id: file.id, progress: 100 }));
      store.dispatch(setStatus({ id: file.id, status: "completed" }));

      setTimeout(() =>
      {
        store.dispatch(removeDownload(file.id));
        this.tasks.delete(file.id);
      }, 2000);
    } catch (error: any)
    {
      if (error.name === "CanceledError" || axios.isCancel(error))
      {
        store.dispatch(setStatus({ id: file.id, status: "paused" }));
      } else
      {
        console.error(error);
        store.dispatch(setStatus({ id: file.id, status: "error" }));

        setTimeout(() =>
        {
          store.dispatch(removeDownload(file.id));
          this.tasks.delete(file.id);
        }, 2000);
      }
    }
  }

  pause(id: string): boolean
  {
    const task = this.tasks.get(id);
    if (!task || task.abortController === null)
    {
      return false;
    }

    task.abortController.abort();
    task.abortController = null;
    store.dispatch(setStatus({ id, status: "paused" }));
    return true;
  }

  resume(id: string): void
  {
    const task = this.tasks.get(id);
    if (!task)
    {
      return;
    }

    // If already downloading, do nothing
    if (task.abortController !== null)
    {
      return;
    }

    // Start new download from current progress
    this.downloadFile(
      { id: task.id, name: task.name, url: task.url },
      task.downloadedBytes,
    );
  }

  cancelAll()
  {
    this.tasks.forEach((task) =>
    {
      if (task.abortController)
      {
        task.abortController.abort();
      }
    });
    this.tasks.clear();
  }

  cancelById(id: string)
  {
    const task = this.tasks.get(id);
    if (task)
    {
      if (task.abortController)
      {
        task.abortController.abort();
      }
      store.dispatch(removeDownload(id));
      this.tasks.delete(id);
    }
  }

  isDownloading(id: string): boolean
  {
    const task = this.tasks.get(id);
    return task !== undefined && task.abortController !== null;
  }

  isPaused(id: string): boolean
  {
    const task = this.tasks.get(id);
    return task !== undefined && task.abortController === null;
  }

  private extractFilenameFromUrl(url: string): string | null
  {
    try
    {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const lastSegment = pathname.split("/").pop();
      if (lastSegment && lastSegment.includes("."))
      {
        return lastSegment;
      }
    } catch (e)
    {
      // Ignore URL parsing errors
    }
    return null;
  }

  private triggerDownload(blob: Blob, filename: string)
  {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(link.href);
  }

  private resolveFilename(
    file: { name: string; url: string },
    response: any,
  ): string
  {
    const disposition = response.headers["content-disposition"];

    if (disposition)
    {
      const utfMatch = disposition.match(/filename\*=UTF-8''(.+)/);
      if (utfMatch) return decodeURIComponent(utfMatch[1]);

      const asciiMatch = disposition.match(/filename="(.+)"/);
      if (asciiMatch) return asciiMatch[1];
    }

    // fallback theo content-type
    const mime = response.headers["content-type"];

    const map: Record<string, string> = {
      "application/zip": ".zip",
      "application/pdf": ".pdf",
    };

    let filename = file.name || "download";

    if (mime && map[mime])
    {
      const ext = map[mime];

      if (!filename.endsWith(ext))
      {
        filename = filename.replace(/\.[^/.]+$/, "") + ext;
      }
    }

    return filename;
  }
}

export const downloadManager = new DownloadManager();
